import { createContext, useContext, useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { usePublicAuth } from './PublicAuthContext.jsx';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { user } = usePublicAuth();
  const [theme, setThemeState] = useState('light');
  const [loading, setLoading] = useState(true);

  // Apply theme to HTML element
  const applyTheme = themeToApply => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;

    // Remove both classes
    root.classList.remove('light', 'dark');

    // Add the new theme class
    root.classList.add(themeToApply);
  };

  // Load theme from Firebase on mount and when user changes
  useEffect(() => {
    const loadTheme = async () => {
      setLoading(true);
      if (typeof window === 'undefined') {
        setLoading(false);
        return;
      }

      try {
        if (user?.uid) {
          // Load from Firebase
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const savedTheme = userData?.theme || 'light';
            setThemeState(savedTheme);
            applyTheme(savedTheme);
          } else {
            // Default to light if no user doc
            setThemeState('light');
            applyTheme('light');
          }
        } else {
          // Not logged in, use localStorage as fallback
          const saved = localStorage.getItem('flexy-theme');
          const initial = saved === 'dark' ? 'dark' : 'light';
          setThemeState(initial);
          applyTheme(initial);
        }
      } catch (error) {
        console.error('Error loading theme:', error);
        // Fallback to localStorage
        const saved = localStorage.getItem('flexy-theme');
        const initial = saved === 'dark' ? 'dark' : 'light';
        setThemeState(initial);
        applyTheme(initial);
      } finally {
        setLoading(false);
      }
    };

    loadTheme();
  }, [user?.uid]);

  // Save theme to Firebase and apply it
  useEffect(() => {
    if (loading) return;

    applyTheme(theme);

    const saveTheme = async () => {
      try {
        if (user?.uid) {
          // Save to Firebase
          await setDoc(
            doc(db, 'users', user.uid),
            {
              theme,
              themeUpdatedAt: serverTimestamp(),
            },
            { merge: true },
          );
          localStorage.setItem('flexy-theme', theme);
        } else {
          // Fallback to localStorage if not logged in
          localStorage.setItem('flexy-theme', theme);
        }
      } catch (error) {
        console.error('Error saving theme:', error);
        // Fallback to localStorage
        try {
          localStorage.setItem('flexy-theme', theme);
        } catch (localError) {
          console.error('Failed to save theme to localStorage:', localError);
        }
      }
    };

    saveTheme();
  }, [theme, user?.uid, loading]);

  const setTheme = newTheme => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setThemeState(newTheme);
    }
  };

  const toggleTheme = () => {
    setThemeState(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
};
